/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { useCalendarDataContext } from './CalendarDataContext';
import { useSidebarContext } from './SidebarContext';
import { useVisibleEntities } from '../hooks/useVisibleEntities';
import { toDateString, getDurationInDays, addDays } from '../utils/dateUtils';
import { ENTITY_TYPE, DRAG_ACTION } from '../constants/types';

export const DragContext = createContext(null);

/**
 * Provider for drag and drop state - manages event/milestone dragging and resizing
 */
export function DragProvider({ children, onClearSelection }) {
  const { events, updateEvent, updateMilestone } = useCalendarDataContext();
  const sidebarState = useSidebarContext();
  const { visibleEvents, visibleMilestones } = useVisibleEntities(sidebarState);
  
  // Batch dragInfo and dragPreview into single state to reduce re-renders
  const [dragState, setDragState] = useState({ info: null, preview: null });
  const dragInfoRef = useRef(null);
  
  /**
   * Calculate preview dates for drag operations.
   */
  const calculatePreviewDates = useCallback((dragData, targetDate) => {
    if (!dragData || !targetDate) return null;
    
    const { type, action, startDate, endDate, clickOffset = 0, lockDuration = false } = dragData;
    const targetDateStr = toDateString(targetDate);
    
    if (type === ENTITY_TYPE.EVENT) {
      if (action === DRAG_ACTION.MOVE) {
        const newStart = addDays(targetDate, -clickOffset);
        const duration = getDurationInDays(startDate, endDate);
        const newEnd = addDays(newStart, duration);
        
        return {
          startDate: toDateString(newStart),
          endDate: toDateString(newEnd)
        };
      } else if (action === DRAG_ACTION.RESIZE_START) {
        if (lockDuration) {
          const duration = getDurationInDays(startDate, endDate);
          const newEnd = addDays(targetDate, duration);
          
          return {
            startDate: targetDateStr,
            endDate: toDateString(newEnd)
          };
        } else {
          const [ey, em, ed] = endDate.split('-').map(Number);
          const endDateObj = new Date(ey, em - 1, ed);
          
          if (targetDate <= endDateObj) {
            return { startDate: targetDateStr, endDate };
          }
          return { startDate: endDate, endDate };
        }
      } else if (action === DRAG_ACTION.RESIZE_END) {
        if (lockDuration) {
          const duration = getDurationInDays(startDate, endDate);
          const newStart = addDays(targetDate, -duration);
          
          return {
            startDate: toDateString(newStart),
            endDate: targetDateStr
          };
        } else {
          const [sy, sm, sd] = startDate.split('-').map(Number);
          const startDateObj = new Date(sy, sm - 1, sd);
          
          if (targetDate >= startDateObj) {
            return { startDate, endDate: targetDateStr };
          }
          return { startDate, endDate: startDate };
        }
      }
    } else if (type === ENTITY_TYPE.MILESTONE) {
      return { date: targetDateStr };
    }
    
    return null;
  }, []);
  
  /**
   * Find the day cell element at a given screen position.
   */
  const findDayCellAtPoint = useCallback((clientX, clientY) => {
    const elements = document.elementsFromPoint(clientX, clientY);
    
    for (const el of elements) {
      if (el.classList.contains('event-bar--preview') || 
          el.classList.contains('event-bar--hidden') ||
          el.classList.contains('milestone--preview') ||
          el.classList.contains('milestone--hidden') ||
          el.classList.contains('event-bar') ||
          el.classList.contains('milestone') ||
          el.classList.contains('event-bar__resize-handle') ||
          el.classList.contains('event-bar__label')) {
        continue;
      }
      
      if (el.hasAttribute('data-date')) {
        return el;
      }
      const parent = el.closest('[data-date]');
      if (parent) {
        return parent;
      }
    }
    return null;
  }, []);
  
  const handleDragStart = useCallback((dragData, mouseEvent) => {
    // Clear selection when drag starts
    if (onClearSelection) {
      onClearSelection();
    }
    
    dragInfoRef.current = dragData;
    
    let originalItem = null;
    if (dragData.type === ENTITY_TYPE.EVENT) {
      originalItem = visibleEvents.find(e => e.id === dragData.id);
    } else if (dragData.type === ENTITY_TYPE.MILESTONE) {
      originalItem = visibleMilestones.find(m => m.id === dragData.id);
    }
    
    const dayCell = findDayCellAtPoint(mouseEvent.clientX, mouseEvent.clientY);
    
    let previewData = null;
    if (dayCell) {
      const dateStr = dayCell.getAttribute('data-date');
      if (dateStr) {
        const [y, m, d] = dateStr.split('-').map(Number);
        const targetDate = new Date(y, m - 1, d);
        targetDate.setHours(0, 0, 0, 0);
        
        const previewDates = calculatePreviewDates(dragData, targetDate);
        previewData = {
          type: dragData.type,
          id: dragData.id,
          action: dragData.action,
          targetDate,
          originalItem,
          ...previewDates
        };
      }
    }
    
    // Single batched state update
    setDragState({
      info: dragData,
      preview: previewData
    });
  }, [calculatePreviewDates, findDayCellAtPoint, visibleEvents, visibleMilestones, onClearSelection]);
  
  const handleDragMove = useCallback((mouseEvent) => {
    if (!dragInfoRef.current) return;
    
    let originalItem = null;
    if (dragInfoRef.current.type === ENTITY_TYPE.EVENT) {
      originalItem = visibleEvents.find(e => e.id === dragInfoRef.current.id);
    } else if (dragInfoRef.current.type === ENTITY_TYPE.MILESTONE) {
      originalItem = visibleMilestones.find(m => m.id === dragInfoRef.current.id);
    }
    
    const dayCell = findDayCellAtPoint(mouseEvent.clientX, mouseEvent.clientY);
    
    if (dayCell) {
      const dateStr = dayCell.getAttribute('data-date');
      if (dateStr) {
        const [y, m, d] = dateStr.split('-').map(Number);
        const targetDate = new Date(y, m - 1, d);
        targetDate.setHours(0, 0, 0, 0);
        
        const previewDates = calculatePreviewDates(dragInfoRef.current, targetDate);
        
        // Update only preview, keep info unchanged
        setDragState(prev => ({
          ...prev,
          preview: {
            type: dragInfoRef.current.type,
            id: dragInfoRef.current.id,
            action: dragInfoRef.current.action,
            targetDate,
            originalItem,
            ...previewDates
          }
        }));
      }
    }
  }, [calculatePreviewDates, findDayCellAtPoint, visibleEvents, visibleMilestones]);
  
  const handleDragEnd = useCallback((targetDate) => {
    const currentDragInfo = dragInfoRef.current;
    const currentDragPreview = dragState.preview; // Capture preview before clearing
    
    // Single batched state clear
    setDragState({ info: null, preview: null });
    dragInfoRef.current = null;
    
    // Use targetDate if provided, otherwise fall back to last preview position
    let dropDate = targetDate;
    if (!dropDate && currentDragPreview?.targetDate) {
      dropDate = currentDragPreview.targetDate;
    }
    
    if (dropDate && currentDragInfo) {
      try {
        const { type, id, action, startDate, endDate, clickOffset = 0 } = currentDragInfo;
        const targetDateStr = toDateString(dropDate);
        
        if (type === ENTITY_TYPE.EVENT) {
          if (action === DRAG_ACTION.MOVE) {
            const newStart = addDays(dropDate, -clickOffset);
            const duration = getDurationInDays(startDate, endDate);
            const newEnd = addDays(newStart, duration);
            
            updateEvent(id, {
              startDate: toDateString(newStart),
              endDate: toDateString(newEnd)
            });
          } else if (action === DRAG_ACTION.RESIZE_START) {
            const eventData = events.find(e => e.id === id);
            
            if (eventData && eventData.lockDuration) {
              const duration = getDurationInDays(startDate, endDate);
              const newEnd = addDays(dropDate, duration);
              
              updateEvent(id, {
                startDate: targetDateStr,
                endDate: toDateString(newEnd)
              });
            } else {
              const [ey, em, ed] = endDate.split('-').map(Number);
              const endDateObj = new Date(ey, em - 1, ed);
              
              if (dropDate <= endDateObj) {
                updateEvent(id, { startDate: targetDateStr });
              }
            }
          } else if (action === DRAG_ACTION.RESIZE_END) {
            const eventData = events.find(e => e.id === id);
            
            if (eventData && eventData.lockDuration) {
              const duration = getDurationInDays(startDate, endDate);
              const newStart = addDays(dropDate, -duration);
              
              updateEvent(id, {
                startDate: toDateString(newStart),
                endDate: targetDateStr
              });
            } else {
              const [sy, sm, sd] = startDate.split('-').map(Number);
              const startDateObj = new Date(sy, sm - 1, sd);
              
              if (dropDate >= startDateObj) {
                updateEvent(id, { endDate: targetDateStr });
              }
            }
          }
        } else if (type === ENTITY_TYPE.MILESTONE) {
          updateMilestone(id, { date: targetDateStr });
        }
      } catch (error) {
        console.error('Drop error:', error);
      }
    }
  }, [dragState.preview, events, updateEvent, updateMilestone]);
  
  const handleDrop = useCallback((targetDate) => {
    handleDragEnd(targetDate);
  }, [handleDragEnd]);
  
  const value = useMemo(() => ({
    dragInfo: dragState.info,
    dragPreview: dragState.preview,
    dragInfoRef,
    findDayCellAtPoint,
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    onDrop: handleDrop,
  }), [
    dragState.info,
    dragState.preview,
    findDayCellAtPoint,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDrop,
  ]);
  
  return (
    <DragContext.Provider value={value}>
      {children}
    </DragContext.Provider>
  );
}

/**
 * Hook to access drag context
 */
export function useDragContext() {
  const context = useContext(DragContext);
  if (!context) {
    throw new Error('useDragContext must be used within DragProvider');
  }
  return context;
}
